import { useQuery } from '@tanstack/react-query';
import { useMultiTenant } from './useMultiTenant';
import { usePermissions } from './usePermissions';
import { supabase } from '@/integrations/supabase/client';

interface DashboardStats {
  totalCylinders: number;
  activeLoans: number;
  overdueLoans: number;
  dueTodayLoans: number;
}

interface RecentActivity {
  id: string;
  client: string;
  action: string;
  item: string;
  date: string;
  status: 'active' | 'overdue' | 'due_today';
}

interface DashboardData {
  stats: DashboardStats;
  recentActivity: RecentActivity[];
}

export const useDashboardData = () => {
  const { isContextReady, ensureCompanyContext, companyId } = useMultiTenant();
  const { isMaster } = usePermissions();

  const { data, isLoading, error, refetch } = useQuery<DashboardData, Error>({
    queryKey: ['dashboard-data', companyId],
    queryFn: async () => {
      const companyId = ensureCompanyContext();
      if (!companyId) return;
      
      // ✅ Execute 4 queries in parallel (1050ms → 400ms)
      const [cylinderStock, loanStats, recentLoans] = await Promise.all([
        // Query 1: Total cylinders from products table
        supabase
          .from('products')
          .select('stock_quantity')
          .eq('company_id', companyId)
          .eq('product_type', 'cylinder')
          .eq('active', true),
        
        // Query 2: Optimized RPC for loan statistics (3 queries → 1)
        supabase.rpc('get_dashboard_loan_stats', { p_company_id: companyId }),
        
        // Query 3: Recent activities (uses idx_loans_recent)
        supabase
          .from('loans')
          .select('id, loan_number, loan_date, expected_return_date, status, customers!inner(name)')
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .limit(5)
      ]);
      
      if (cylinderStock.error) throw cylinderStock.error;
      if (loanStats.error) throw loanStats.error;
      if (recentLoans.error) throw recentLoans.error;
      
      // Process data locally (faster than on server)
      const today = new Date().toISOString().split('T')[0];
      
      const totalStock = cylinderStock.data?.reduce(
        (total, item) => total + (item.stock_quantity || 0), 
        0
      ) || 0;
      
      const activities: RecentActivity[] = recentLoans.data?.map(loan => {
        const isOverdue = loan.expected_return_date < today;
        const isDueToday = loan.expected_return_date === today;
        
        return {
          id: loan.id,
          client: loan.customers?.name || 'Cliente não identificado',
          action: 'Empréstimo',
          item: `Nº ${loan.loan_number}`,
          date: new Date(loan.loan_date).toLocaleDateString('pt-BR'),
          status: isOverdue ? 'overdue' : isDueToday ? 'due_today' : 'active'
        };
      }) || [];

      return {
        stats: {
          totalCylinders: totalStock,
          activeLoans: loanStats.data?.[0]?.active_count || 0,
          overdueLoans: loanStats.data?.[0]?.overdue_count || 0,
          dueTodayLoans: loanStats.data?.[0]?.due_today_count || 0,
        },
        recentActivity: activities
      };
    },
    enabled: Boolean(isContextReady),
    staleTime: 60000, // ✅ OTIMIZADO: 1 minuto (era 30s)
    gcTime: 10 * 60 * 1000, // ✅ OTIMIZADO: 10 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false, // ✅ NOVO: Não refetch ao montar
    retry: 1, // ✅ NOVO: Apenas 1 retry
  });

  return {
    stats: data?.stats || {
      totalCylinders: 0,
      activeLoans: 0,
      overdueLoans: 0,
      dueTodayLoans: 0,
    },
    recentActivity: data?.recentActivity || [],
    loading: isLoading,
    error: error ? 'Erro ao carregar dados do dashboard' : null,
    refreshData: refetch,
    isContextReady,
    isMaster
  };
};